import { MethydTargetInfo } from '@ais-ui/websockets';
import React from 'react';
import { useSettings } from '../../Settings/SettingsProvider';
import {
  InfoTooltipFields,
  type TooltipFieldDefinition,
} from './InfoTooltipFields';

interface MethydInfoTooltipProps {
  methydInfo: MethydTargetInfo;
}

export const MethydInfoTooltip = ({ methydInfo }: MethydInfoTooltipProps) => {
  const { settings } = useSettings();
  const ribbonSettings = settings['ribbonPlugin.settings'];

  const fields: TooltipFieldDefinition[] = [
    // Air-related fields
    {
      key: 'airPressureTendency',
      label: 'Air Pressure Tendency',
      value: methydInfo.airPressureTendency,
      visible: ribbonSettings?.show_methyd_air_related,
    },
    {
      key: 'airPressure',
      label: 'Air Pressure',
      value: methydInfo.airPressure,
      visible: ribbonSettings?.show_methyd_air_related,
    },
    {
      key: 'airTemperature',
      label: 'Air Temperature',
      value: methydInfo.airTemperature,
      visible: ribbonSettings?.show_methyd_air_related,
    },
    {
      key: 'averageWindSpeed',
      label: 'Average Wind Speed',
      value: methydInfo.averageWindSpeed,
      visible: ribbonSettings?.show_methyd_air_related,
    },
    // Water-related fields
    {
      key: 'currentDirection2',
      label: 'Current Direction #2',
      value: methydInfo.currentDirection2,
      visible: ribbonSettings?.show_methyd_water_related,
    },
    {
      key: 'currentDirection3',
      label: 'Current Direction #3',
      value: methydInfo.currentDirection3,
      visible: ribbonSettings?.show_methyd_water_related,
    },
    {
      key: 'currentMeasuringLevel2',
      label: 'Current Measuring Level #2',
      value: methydInfo.currentMeasuringLevel2,
      visible: ribbonSettings?.show_methyd_water_related,
    },
    {
      key: 'currentMeasuringLevel3',
      label: 'Current Measuring Level #3',
      value: methydInfo.currentMeasuringLevel3,
      visible: ribbonSettings?.show_methyd_water_related,
    },
    {
      key: 'currentSpeed2',
      label: 'Current Speed #2',
      value: methydInfo.currentSpeed2,
      visible: ribbonSettings?.show_methyd_water_related,
    },
    {
      key: 'currentSpeed3',
      label: 'Current Speed #3',
      value: methydInfo.currentSpeed3,
      visible: ribbonSettings?.show_methyd_water_related,
    },
    {
      key: 'dewPoint',
      label: 'Dew Point',
      value: methydInfo.dewPoint,
      visible: ribbonSettings?.show_methyd_air_related,
    },
    // Metadata fields
    {
      key: 'lastOrigin',
      label: 'Last Origin',
      value: methydInfo.lastOrigin,
      visible: ribbonSettings?.show_methyd_last_origin,
    },
    {
      key: 'location',
      label: 'Location',
      value: methydInfo.location,
      visible: ribbonSettings?.show_methyd_location,
    },
    {
      key: 'message',
      label: 'Message',
      value: methydInfo.message,
      visible: ribbonSettings?.show_methyd_message,
    },
    {
      key: 'metHydLatitude',
      label: 'MetHyd Latitude',
      value: methydInfo.metHydLatitude,
      visible: ribbonSettings?.show_methyd_latitude,
    },
    {
      key: 'metHydLongitude',
      label: 'MetHyd Longitude',
      value: methydInfo.metHydLongitude,
      visible: ribbonSettings?.show_methyd_longitude,
    },
    {
      key: 'latitude',
      label: 'Latitude',
      value: methydInfo.latitude,
      visible: ribbonSettings?.show_latitude_degrees,
    },
    {
      key: 'longitude',
      label: 'Longitude',
      value: methydInfo.longitude,
      visible: ribbonSettings?.show_longitude_degrees,
    },
    {
      key: 'metHydMmsi',
      label: 'MMSI',
      value: methydInfo.metHydMmsi,
      visible: ribbonSettings?.show_methyd_mmsi,
    },
    // More air-related fields
    {
      key: 'relativeHumidity',
      label: 'Relative Humidity',
      value: methydInfo.relativeHumidity,
      visible: ribbonSettings?.show_methyd_air_related,
    },
    // More water-related fields
    {
      key: 'salinity',
      label: 'Salinity',
      value: methydInfo.salinity,
      visible: ribbonSettings?.show_methyd_water_related,
    },
    {
      key: 'significantWaveHeight',
      label: 'Significant Wave Height',
      value: methydInfo.significantWaveHeight,
      visible: ribbonSettings?.show_methyd_water_related,
    },
    {
      key: 'surfaceCurrentDirection',
      label: 'Surface Current Direction',
      value: methydInfo.surfaceCurrentDirection,
      visible: ribbonSettings?.show_methyd_water_related,
    },
    {
      key: 'surfaceCurrentSpeedIncludingTide',
      label: 'Surface Current Speed Including Tide',
      value: methydInfo.surfaceCurrentSpeedIncludingTide,
      visible: ribbonSettings?.show_methyd_water_related,
    },
    {
      key: 'swellDirection',
      label: 'Swell Direction',
      value: methydInfo.swellDirection,
      visible: ribbonSettings?.show_methyd_water_related,
    },
    {
      key: 'swellHeight',
      label: 'Swell Height',
      value: methydInfo.swellHeight,
      visible: ribbonSettings?.show_methyd_water_related,
    },
    {
      key: 'swellPeriod',
      label: 'Swell Period',
      value: methydInfo.swellPeriod,
      visible: ribbonSettings?.show_methyd_water_related,
    },
    {
      key: 'waterLevelIncludingTide',
      label: 'Water Level Including Tide',
      value: methydInfo.waterLevelIncludingTide,
      visible: ribbonSettings?.show_methyd_water_related,
    },
    {
      key: 'waterLevelTrend',
      label: 'Water Level Trend',
      value: methydInfo.waterLevelTrend,
      visible: ribbonSettings?.show_methyd_water_related,
    },
    {
      key: 'waterTemperature',
      label: 'Water Temperature',
      value: methydInfo.waterTemperature,
      visible: ribbonSettings?.show_methyd_water_related,
    },
    {
      key: 'waveDirection',
      label: 'Wave Direction',
      value: methydInfo.waveDirection,
      visible: ribbonSettings?.show_methyd_water_related,
    },
    {
      key: 'wavePeriod',
      label: 'Wave Period',
      value: methydInfo.wavePeriod,
      visible: ribbonSettings?.show_methyd_water_related,
    },
    // More air-related
    {
      key: 'windDirection',
      label: 'Wind Direction',
      value: methydInfo.windDirection,
      visible: ribbonSettings?.show_methyd_air_related,
    },
    {
      key: 'windGustDirection',
      label: 'Wind Gust Direction',
      value: methydInfo.windGustDirection,
      visible: ribbonSettings?.show_methyd_air_related,
    },
    {
      key: 'windGust',
      label: 'Wind Gust',
      value: methydInfo.windGust,
      visible: ribbonSettings?.show_methyd_air_related,
    },
  ];

  return <InfoTooltipFields fields={fields} fallback="Unknown" gap="gap-4" />;
};
